import React, { useState } from 'react';
import { User, Mail, Lock, Check, X, Eye, EyeOff } from 'lucide-react';
import strings from '../../data/translationForm.json';
import './style.css';

const SmartFormDemo = () => {
  const lang = 'en';
  const t = (path) => path.split('.').reduce((acc, key) => acc?.[key], strings[lang]) || '';

  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [validation, setValidation] = useState({ name: null, email: null, password: null });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        return value.trim().length >= 2 ? 'valid' : value.length > 0 ? 'invalid' : null;
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? 'valid' : value.length > 0 ? 'invalid' : null;
      }
      case 'password':
        return value.length >= 6 ? 'valid' : value.length > 0 ? 'invalid' : null;
      default:
        return null;
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidation((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const getPasswordStrength = (pw) => {
    let score = 0;
    if (pw.length >= 6) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    // 0..4 → классы/ярлыки
    return {
      score,
      label: ['empty', 'weak', 'medium', 'good', 'strong'][score],
      className: ['is-0', 'is-1', 'is-2', 'is-3', 'is-4'][score]
    };
  };
  const pw = getPasswordStrength(formData.password);

  const handleSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({ name: '', email: '', password: '' });
      setValidation({ name: null, email: null, password: null });
      setJustSubmitted(true);
      setTimeout(() => setJustSubmitted(false), 2500);
    }, 1200);
  };

  const isFormValid = Object.values(validation).every((v) => v === 'valid');
  const completedCount = Object.values(validation).filter((v) => v === 'valid').length;

  return (
    <div className="smart-form-demo">
      {/* Шаги (1/3) */}
      <div className="form-steps" aria-label="Steps">
        <span className={`dot ${completedCount >= 1 ? 'is-active' : ''}`} />
        <span className="bar" />
        <span className={`dot ${completedCount >= 2 ? 'is-active' : ''}`} />
        <span className="bar" />
        <span className={`dot ${completedCount >= 3 ? 'is-active' : ''}`} />
        <span className="steps-label">Step {Math.max(1, completedCount)}/3</span>
      </div>

      {/* Name */}
      <div className="form-field">
        <div className="field-wrapper">
          <User size={16} className="field-icon" aria-hidden="true" />
          <input
            type="text"
            placeholder={t('placeholders.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`form-input ${validation.name || ''}`}
            aria-invalid={validation.name === 'invalid'}
            aria-describedby={validation.name === 'invalid' ? 'name-error' : undefined}
            autoComplete="name"
          />
          {validation.name && (
            <div className="validation-icon" aria-hidden="true">
              {validation.name === 'valid' ? <Check size={16} className="valid" /> : <X size={16} className="invalid" />}
            </div>
          )}
        </div>
        {validation.name === 'invalid' && (
          <div className="error-message" id="name-error">
            {t('errors.nameMin')}
          </div>
        )}
      </div>

      {/* Email */}
      <div className="form-field">
        <div className="field-wrapper">
          <Mail size={16} className="field-icon" aria-hidden="true" />
          <input
            type="email"
            placeholder={t('placeholders.email')}
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className={`form-input ${validation.email || ''}`}
            aria-invalid={validation.email === 'invalid'}
            aria-describedby={validation.email === 'invalid' ? 'email-error' : undefined}
            autoComplete="email"
            inputMode="email"
          />
          {validation.email && (
            <div className="validation-icon" aria-hidden="true">
              {validation.email === 'valid' ? <Check size={16} className="valid" /> : <X size={16} className="invalid" />}
            </div>
          )}
        </div>
        {validation.email === 'invalid' && (
          <div className="error-message" id="email-error">
            {t('errors.emailInvalid')}
          </div>
        )}
      </div>

      {/* Password */}
      <div className="form-field">
        <div className="field-wrapper">
          <Lock size={16} className="field-icon" aria-hidden="true" />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder={t('placeholders.password')}
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            className={`form-input ${validation.password || ''}`}
            aria-invalid={validation.password === 'invalid'}
            aria-describedby={validation.password === 'invalid' ? 'password-error' : undefined}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          {validation.password && (
            <div className="validation-icon" aria-hidden="true">
              {validation.password === 'valid' ? <Check size={16} className="valid" /> : <X size={16} className="invalid" />}
            </div>
          )}
        </div>
        {/* сила пароля */}
        <span className={`pw-strength ${pw.className}`} aria-label={`Password strength: ${pw.label}`}>
          <i /><i /><i /><i />
        </span>

        {validation.password === 'invalid' && (
          <div className="error-message" id="password-error">
            {t('errors.passwordMin')}
          </div>
        )}
      </div>

      {/* Согласие (GDPR) */}
      <label className="consent-row">
        <input className="consent-checkbox" type="checkbox" />
        <span className="consent-switch" aria-hidden="true"></span>
        <span className="consent-text">I agree to the Privacy Policy</span>
      </label>

      {/* Submit */}
      <button
        className={`submit-btn ${isFormValid ? 'enabled' : 'disabled'} ${isSubmitting ? 'submitting' : ''}`}
        onClick={handleSubmit}
        disabled={!isFormValid || isSubmitting}
      >
        {isSubmitting ? t('buttons.submitting') : t('buttons.submit')}
      </button>

      {/* Trust badges */}
      <div className="trust-row" aria-label="Trust badges">
        <span className="trust-badge"><Lock size={14}/> SSL</span>
        <span className="trust-badge"><Check size={14}/> GDPR</span>
        <span className="trust-badge"><Check size={14}/> No spam</span>
      </div>

      {/* Success (всплывает на 2.5s после сабмита) */}
      <div className={`success-banner ${justSubmitted ? 'show' : ''}`}>
        <Check size={16}/> Account created — check your email
      </div>

      {/* Stats (как было) */}
      <div className="form-stats">
        {t('stats.completed')}: {completedCount}/3
      </div>
    </div>
  );
};

export default SmartFormDemo;
